/**
 * Bank routing identifier formats, keyed by scheme rather than by country.
 *
 * The country tells us which scheme applies; the format is a property of the scheme. Same split as
 * the registration identifiers — a sort code is six digits whoever is asking.
 */
export type BankRoutingScheme = "bsb" | "aba" | "sort_code" | "iban" | "nz_bank_account";

export type BankRoutingSchemeSpec = {
  label: string;
  placeholder: string;
  /** Accepts the value with or without its conventional separators. */
  pattern: RegExp;
  errorMessage: string;
  /** Length of the account number that accompanies it. */
  accountNumberLength: { min: number; max: number };
  /**
   * Whether Stripe collects deposit bank details itself during Connect onboarding. Where it does,
   * the platform's own Deposits form is hidden (the US already worked this way; IBAN and NZ account
   * numbers follow it). Where it does not (BSB, sort code), the form is shown.
   */
  depositsViaStripeOnboarding: boolean;
};

export const BANK_ROUTING_SCHEMES: Record<BankRoutingScheme, BankRoutingSchemeSpec> = {
  bsb: {
    label: "Bank State Branch",
    placeholder: "123-456",
    pattern: /^\d{3}-?\d{3}$/,
    errorMessage: "Invalid BSB",
    accountNumberLength: { min: 4, max: 9 },
    depositsViaStripeOnboarding: false,
  },
  aba: {
    label: "Routing Number",
    placeholder: "123456789",
    pattern: /^\d{9}$/,
    errorMessage: "Invalid routing number (9 digits)",
    accountNumberLength: { min: 4, max: 17 },
    depositsViaStripeOnboarding: true,
  },
  sort_code: {
    label: "Sort Code",
    placeholder: "12-34-56",
    pattern: /^\d{2}-?\d{2}-?\d{2}$/,
    errorMessage: "Invalid sort code (6 digits)",
    accountNumberLength: { min: 8, max: 8 },
    depositsViaStripeOnboarding: false,
  },
  // Switzerland. The IBAN is the account identifier — there is no separate routing number — so the
  // "account number" that accompanies it is the IBAN itself. Stripe collects it during onboarding.
  iban: {
    label: "IBAN",
    placeholder: "CH93 0076 2011 6238 5295 7",
    pattern: /^CH\d{2}(\s?[A-Z0-9]{4}){4}\s?[A-Z0-9]$/i,
    errorMessage: "Invalid IBAN (Swiss IBANs are 21 characters starting with CH)",
    accountNumberLength: { min: 21, max: 26 },
    depositsViaStripeOnboarding: true,
  },
  // New Zealand: bank(2)-branch(4)-account(7)-suffix(2-3), a single identifier with no routing number.
  nz_bank_account: {
    label: "Bank Account Number",
    placeholder: "12-3456-7890123-00",
    pattern: /^\d{2}-?\d{4}-?\d{7}-?\d{2,3}$/,
    errorMessage: "Invalid bank account number (bank-branch-account-suffix)",
    accountNumberLength: { min: 15, max: 19 },
    depositsViaStripeOnboarding: true,
  },
};

/**
 * A scheme the frontend does not know. Previously an unknown scheme silently became a BSB, so a
 * country whose scheme landed in the database before the frontend learned it showed a Swiss or NZ
 * operator an Australian 3-3 digit field. Now it renders a neutral, non-validating spec and hides the
 * deposits form — visibly incomplete rather than confidently wrong.
 */
const UNKNOWN_BANK_ROUTING_SCHEME: BankRoutingSchemeSpec = {
  label: "Bank routing identifier",
  placeholder: "",
  pattern: /^.+$/,
  errorMessage: "Invalid bank routing identifier",
  accountNumberLength: { min: 1, max: 34 },
  depositsViaStripeOnboarding: true,
};

export const getBankRoutingScheme = (scheme?: string): BankRoutingSchemeSpec =>
  BANK_ROUTING_SCHEMES[(scheme ?? "bsb") as BankRoutingScheme] ?? UNKNOWN_BANK_ROUTING_SCHEME;
